/** Posting accounts a1–a4 (WS6 routes): handle, GeeLark phone (protected phones are refused), quota, enabled,
 * artists served. */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, ifMatch, type Schemas, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { useArtists } from "../board/BoardView";
import { useAction } from "../lib/actions";
import { ActionError, QueryView, Section, TableScroll } from "../lib/ui";
import { intParam, useUrlState } from "../lib/url";
import { AdminOnlyNote } from "./AdminOnly";

type Account = Schemas["AccountOut"];

/** Mirrors vp.contracts.enums.GEELARK_DENYLIST (display + early refusal; the server refuses them too). a4's phone
 * 638011400223981772 was released for posting on 2026-10-13. */
export const PROTECTED_PHONES: readonly string[] = ["638040682455367729"];

export function useAccounts() {
  return useQuery({ queryKey: qk.list("accounts"), queryFn: () => unwrap(api.GET("/api/accounts")) });
}

type Draft = { handle: string; phone: string; quota: string; enabled: boolean; artists: number[] };
const draftOf = (a: Account): Draft => ({ handle: a.handle ?? "", phone: a.geelark_phone_id ?? "", quota: String(a.quota_per_day),
  enabled: a.enabled, artists: [...a.artists] });

/** Mirrors vp.distribution.accounts.HANDLE_RE, applied after the same normalisation (trim, one leading @ dropped). */
export const HANDLE_RE = /^[A-Za-z0-9._]{2,24}$/;
export const HANDLE_HELP = "2–24 letters, digits, '.' or '_' (a leading @ is fine; empty clears it)";

export function normalizeHandle(h: string): string {
  const s = h.trim();
  return s.startsWith("@") ? s.slice(1) : s;
}

export function accountProblems(d: Draft): string[] {
  const out: string[] = [];
  if (PROTECTED_PHONES.includes(d.phone.trim())) out.push(`geelark_phone_id: ${d.phone.trim()} is a protected phone and is refused`);
  const q = intParam(d.quota);
  if (q === undefined || q < 0 || q > 20) out.push("quota_per_day: whole number 0–20");
  const h = normalizeHandle(d.handle);
  if (h && !HANDLE_RE.test(h)) out.push(`handle: ${HANDLE_HELP}, e.g. vzs.clips`);
  return out;
}

function EditAccount({ a, onClose }: { a: Account; onClose: () => void }) {
  const qc = useQueryClient();
  const artists = useArtists();
  const [d, setD] = useState(draftOf(a));
  const [artistIds, setArtistIds] = useState(a.artists.join(","));
  const problems = accountProblems(d);
  const save = useAction(
    () => unwrap(api.PATCH("/api/accounts/{account_id}", {
      params: { path: { account_id: a.id } },
      body: { handle: normalizeHandle(d.handle) || null, geelark_phone_id: d.phone.trim() || null, quota_per_day: Number(d.quota),
        enabled: d.enabled, artists: artists.isError ? artistIds.split(",").map((x) => intParam(x.trim())).filter((x): x is number => x !== undefined) : d.artists },
      headers: ifMatch(a.version),
    })),
    { label: `Save account ${a.id}`, success: (r) => `Saved account ${r.id} (version ${r.version})`, invalidate: [qk.list("accounts"), qk.list("calendar")] },
  );
  return (
    <form className="ws7-form" aria-label={`Edit account ${a.id}`} onSubmit={async (e) => {
      e.preventDefault();
      if (!problems.length && (await save.run())) onClose();
    }}>
      <div className="ws7-toolbar">
        <label className="ws7-field">
          Handle
          <input value={d.handle} data-testid={`account-${a.id}-handle`} aria-invalid={problems.some((p) => p.startsWith("handle"))}
            aria-describedby={`account-${a.id}-handle-help`} onChange={(e) => setD({ ...d, handle: e.target.value })} />
        </label>
        <span className="ws7-muted" id={`account-${a.id}-handle-help`}>Handle: {HANDLE_HELP}</span>
        <label className="ws7-field">
          GeeLark phone id
          <input value={d.phone} inputMode="numeric" data-testid={`account-${a.id}-phone`} aria-invalid={problems.some((p) => p.startsWith("geelark"))}
            onChange={(e) => setD({ ...d, phone: e.target.value })} />
        </label>
        <label className="ws7-field">
          Posts per day
          <input type="number" min={0} max={20} step={1} value={d.quota} data-testid={`account-${a.id}-quota`}
            onChange={(e) => setD({ ...d, quota: e.target.value })} />
        </label>
        <label className="ws7-check">
          <input type="checkbox" checked={d.enabled} data-testid={`account-${a.id}-enabled`} onChange={(e) => setD({ ...d, enabled: e.target.checked })} />
          Enabled
        </label>
      </div>
      <fieldset className="ws7-setting-group">
        <legend>Artists served by {a.id}</legend>
        {artists.isError ? (
          <label className="ws7-field">
            Artist ids (comma-separated)
            <input value={artistIds} data-testid={`account-${a.id}-artist-ids`} onChange={(e) => setArtistIds(e.target.value)} />
          </label>
        ) : (
          <div className="ws7-row">
            {(artists.data ?? []).map((ar) => (
              <label key={ar.id} className="ws7-check">
                <input type="checkbox" checked={d.artists.includes(ar.id)} data-testid={`account-${a.id}-artist-${ar.id}`}
                  onChange={(e) => setD({ ...d, artists: e.target.checked ? [...d.artists, ar.id] : d.artists.filter((x) => x !== ar.id) })} />
                {ar.name}
              </label>
            ))}
          </div>
        )}
      </fieldset>
      {problems.length > 0 && (
        <div role="alert" data-testid={`account-${a.id}-problems`}>
          <ul className="ws7-field-error">{problems.map((p) => <li key={p}>{p}</li>)}</ul>
        </div>
      )}
      <ActionError error={save.error} onReload={async () => {
        await qc.invalidateQueries({ queryKey: qk.list("accounts") });
        save.clear();
        onClose();
      }} />
      <div className="ws7-row">
        <button type="submit" className="ws7-primary" data-testid={`account-save-${a.id}`} disabled={save.pending || problems.length > 0}>
          Save {a.id}
        </button>
        <button type="button" data-testid={`account-cancel-${a.id}`} onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
}

export function AccountsTab({ admin }: { admin: boolean }) {
  const accounts = useAccounts();
  const artists = useArtists();
  const names = new Map((artists.data ?? []).map((a) => [a.id, a.name]));
  const { sp, set } = useUrlState();
  const editing = sp.get("edit");
  return (
    <Section id="accounts" title="Posting accounts">
      <p className="ws7-muted" data-testid="protected-phones">
        Protected GeeLark phones (never driven; the server refuses them): {PROTECTED_PHONES.join(", ")}
      </p>
      {!admin && <AdminOnlyNote what="editing accounts" testId="accounts-admin-only" />}
      <QueryView q={accounts} what="accounts">
        {(rows) => (
          <TableScroll label="Account table">
            <table className="ws7-table" aria-label="Accounts" data-testid="accounts-table">
              <thead>
                <tr>
                  <th scope="col">Account</th><th scope="col">Handle</th><th scope="col">GeeLark phone</th>
                  <th scope="col" className="num">Posts/day</th><th scope="col">Enabled</th><th scope="col">Artists</th>
                  <th scope="col" className="num">Followers</th><th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  admin && editing === a.id ? (
                    <tr key={a.id} data-testid={`account-row-${a.id}`}>
                      <th scope="row">{a.id}</th>
                      <td colSpan={7}><EditAccount a={a} onClose={() => set({ edit: undefined })} /></td>
                    </tr>
                  ) : (
                    <tr key={a.id} data-testid={`account-row-${a.id}`}>
                      <th scope="row">{a.id}</th>
                      <td>{a.handle ?? <span className="ws7-muted">not set</span>}</td>
                      <td className="ws7-mono">{a.geelark_phone_id ?? <span className="ws7-muted">not set</span>}</td>
                      <td className="num">{a.quota_per_day}</td>
                      <td>{a.enabled ? "yes" : "no"}</td>
                      <td>{a.artists.map((id) => names.get(id) ?? `#${id}`).join(", ") || <span className="ws7-muted">none</span>}</td>
                      <td className="num">{a.followers ?? ""}</td>
                      <td>
                        {admin ? (
                          <button type="button" data-testid={`account-edit-${a.id}`} aria-label={`Edit account ${a.id}`} onClick={() => set({ edit: a.id })}>
                            Edit
                          </button>
                        ) : <span className="ws7-muted">admin only</span>}
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
      </QueryView>
    </Section>
  );
}
